import React, { useEffect, useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useParams, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Navbar from "./components/Navbar";

export default function TransactionList() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [transactionID, setTransactionID] = useState(null);
  const API_URL = "/api";

  function formatNumberUS(value) {
    return Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const fetchDataList = async () => {
    try {
      const response = await fetch(
        `${API_URL}/getTransactionDetailsListByCompanyId/${id}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Transaction List Data:", data);
      setTransactionID(data.TRANSACTIONID);

      setItems(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchDataList();
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "Sl no.",
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Borrower",
        accessorFn: (row) => row.BORROWER || "N/A",
      },
      {
        header: "Loan Amount (HKD)",
        accessorFn: (row) => row.LOANAMOUNT_HK || "N/A",
      },
      {
        header: "Lender",
        accessorFn: (row) => row.LENDER || "N/A",
      },
      {
        header: "Security Type",
        accessorFn: (row) => row.SECURITYTYPE || "N/A",
      },
      {
        header: "Transaction ID",
        accessorFn: (row) => row.TRANSACTIONID || "N/A",
      },

      {
        header: "View Details",
        cell: ({ row }) => {
          const companyId = row.original.TRANSACTIONID;
          return (
            <Link
              to={companyId ? `/ViewTransactionList/${companyId}` : ``}
              className="btn btn-info btn-sm"
            >
              View Details
            </Link>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: items,
    columns,
    state: {
      globalFilter: searchText,
    },
    onGlobalFilterChange: setSearchText,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 50, // 👈 Default rows per page
      },
    },
  });

  return (
    <div className="app-container">
      <div className="container-fluid">
        <main className="content">
          <div className="row">
            <Navbar />
            <div className="col-md-10">
              <div className="row mt-4">
                <div className="col-md-8">
                  <h3>Transaction List</h3>
                </div>
                <div className="col-md-4">
                  {/* Search Box */}
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by company name, registration number, place..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-md-12">
                  <table className="table table-bordered table-hover table-sm">
                    <thead className="table-light">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              style={{ cursor: "pointer" }}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: " 🔼",
                                desc: " 🔽",
                              }[header.column.getIsSorted()] ?? null}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id} style={{ textAlign: "right" }}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center">
                            Loading...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div>
                      Page{" "}
                      <strong>
                        {table.getState().pagination.pageIndex + 1} of{" "}
                        {table.getPageCount()}
                      </strong>
                    </div>

                    <div className="btn-group">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                      >
                        Previous
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                      >
                        Next
                      </button>
                    </div>

                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={table.getState().pagination.pageSize}
                      onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                      }}
                    >
                      {[5, 10, 20, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          Show {pageSize}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
